import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Send, Transform } from '@mui/icons-material';
import ResourceDetailHeader from 'js/components/ResourceDetailHeader/ResourceDetailHeader';
import { updateDataSetInfo } from 'js/actions';
import { Stack } from 'new/elements/Stack';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import styles from 'js/components/data_sets/cards/DataSetDetailHeader.module.scss';
import { Button } from 'new/elements/Button';
import { SearchFilterTypes } from 'js/constants/visibilityFilters';
import ViewEntityPipelineButtonContainer from 'js/components/pipelines/cards/ViewEntityPipelineButtonContainer';
import DataSetDetailActionsContainer from 'js/components/data_sets/cards/DataSetDetailActionsContainer';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';

interface DataSetDetailHeaderProps {
  readOnly: boolean;
  adminAccess: boolean;
  data: any;
  shared?: boolean;
  hideActions: boolean;
  isEditable: any;
  isDetected: boolean;
  disableEditMessage: any;
  isManaged: any;
  onCloseDrawer?: () => void;
  connectionOrVendorType: any;
  statsContentView: any;
  showPipelineButton?: boolean;
  showTransform?: boolean;
  showSend?: boolean;
  showMenu?: boolean;
}

const Connection = ResourceDetailHeader.TopContent.Connection;
const ViewFlowButton = ResourceDetailHeader.TopContent.ViewFlowButton;

export const AvailableDataSetDetailHeader = (props: DataSetDetailHeaderProps) => {
  const {
    readOnly,
    adminAccess,
    data,
    showMenu,
    isEditable,
    isDetected,
    hideActions,
    disableEditMessage,
    isManaged,
    showPipelineButton = true,
    showTransform,
    showSend,
    statsContentView,
    connectionOrVendorType,
    onCloseDrawer,
  } = props;
  const dispatch = useAppDispatch();

  const publicDatasets = useSelector((state: any) => state.publicDatasets);
  const sharedDatasets = useSelector((state: any) => state.sharedDatasets);

  const updateInfo = (id, info) => {
    dispatch(updateDataSetInfo(data.id, info));
  };

  const shared = useMemo(
    () => publicDatasets.byId.includes(data.id) || sharedDatasets.byId.includes(data.id),
    [data, publicDatasets, sharedDatasets],
  );

  return (
    <ResourceDetailHeader
      readOnly={readOnly}
      adminAccess={adminAccess}
      data={data}
      updateInfo={updateInfo}
      onCloseDrawer={onCloseDrawer}
    >
      <ResourceDetailHeader.TopContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap="16px">
          <Stack direction="row" alignItems="center" gap="16px">
            <Connection
              resourceType={RESOURCE_TYPE_DATA_SET}
              data={{ ...data, ...props, shared: true, isDetected }}
            />
            <ChipWithCopy tooltipTitle="Copy Nexset ID" toCopy={data?.id}>
              ID {data?.id}
            </ChipWithCopy>
            <div className={styles.boxInfo}>
              <PipelineStatusBadge
                id={data.id}
                status={data.status as Status}
                parentArchivalStatus={data?.archival_status}
              />
            </div>
          </Stack>
          <Stack direction="row" alignItems="center" gap="16px">
            {showPipelineButton && !shared && (
              <ViewEntityPipelineButtonContainer
                filterType={SearchFilterTypes.NEXSET_ID}
                filterValue={data.id}
                className={'view-entity-pipeline-button-position'}
                ButtonComponent={<ViewFlowButton />}
              />
            )}
            {!hideActions && (
              <DataSetDetailActionsContainer
                data={data}
                showMenu={showMenu !== undefined ? showMenu : !shared}
                TransformComponent={
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={{ backgroundColor: 'white' }}
                    className={styles.transformButton}
                    endIcon={<Transform />}
                  >
                    Transform
                  </Button>
                }
                SendComponent={
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={{ backgroundColor: 'white' }}
                    className={styles.sendButton}
                    endIcon={<Send />}
                  >
                    Send
                  </Button>
                }
                showTransform={showTransform}
                showSend={!shared && (showSend != undefined ? showSend : true)}
                isDetected={isDetected}
                showStatus={false}
                disableEditing={!isEditable}
                disableEditMessage={disableEditMessage}
                readOnly={readOnly}
                isManaged={isManaged}
                connectionOrVendorType={connectionOrVendorType}
              />
            )}
          </Stack>
        </Stack>
      </ResourceDetailHeader.TopContent>
      {statsContentView}
    </ResourceDetailHeader>
  );
};
